import type {
  InvitationOrganization,
} from '@/modules/auth/use-cases'
import type { LegalDocument, LoadCurrentDocument } from '@/shared/domain'
import { Button } from '@/shared/ui/primitives'
import { RegisterStepCard, RegisterStepHeading } from '@/modules/auth/ui/register/components'
import { AddressSection } from './address-section'
import { DateOfBirthSection } from './date-of-birth-section'
import { EmergencyContactsSection } from './emergency-contacts-section'
import type { DriversLicenseEntry, JoinFormValues, LicenseEntry } from './form-values'
import { LicenseCollectionSection } from './license-collection-section'
import { LiabilityWaiverSection, type LiabilityWaiverSectionProps } from './liability-waiver-section'
import { PersonalInfoSection } from './personal-info-section'
import { VehicleInfoSection } from './vehicle-info-section'
import { DriversLicenseSection } from './drivers-license-section'
import type { JoinStep } from '../join-step-types'

export type RegistrationFormProps = {
  organization: InvitationOrganization
  values: JoinFormValues
  errors: Record<string, string>
  isSubmitting: boolean
  isMinor: boolean
  age?: number
  emailLocked: boolean
  phoneRequired: boolean
  phoneLocked: boolean
  canLinkExistingAccount: boolean
  linkedEmail: string | null
  onLinkExistingAccount: () => void
  tosAccepted: boolean
  privacyAccepted: boolean
  legalAlreadyAccepted: boolean
  steps: JoinStep[]
  currentStepIndex: number
  currentStep: JoinStep
  isLastStep: boolean
  isStepSkippable: boolean
  canGoBack: boolean
  isCurrentStepComplete: boolean
  goBack: () => void
  goNext: () => void
  onSkip: () => void
  licenseEntries: Record<string, LicenseEntry>
  driversLicense?: DriversLicenseEntry
  onDriversLicenseChange: (entry: DriversLicenseEntry) => void
  onFieldChange: (field: keyof JoinFormValues, value: string) => void
  onLicenseChange: (licenseType: string, field: keyof LicenseEntry, value: string | boolean) => void
  onTosChange: (value: boolean) => void
  onPrivacyChange: (value: boolean) => void
  onSubmit: () => void
  onLoadCurrentDocument?: LoadCurrentDocument
  onDocumentsLoaded?: (documents: LegalDocument[]) => void
  waiver: LiabilityWaiverSectionProps
}

const STEP_SUBTITLES: Record<string, (orgName: string) => string> = {
  personal_info: () => 'Tell us a bit about yourself.',
  date_of_birth: () => 'What is your date of birth?',
  address: (orgName) => `Where can ${orgName} reach you?`,
  emergency_contacts: (orgName) => `Who should ${orgName} contact in an emergency?`,
  hunting_licenses: (orgName) => `Provide the hunting license information ${orgName} requires.`,
  vehicle_info: (orgName) => `Tell ${orgName} about your vehicle.`,
  drivers_license: (orgName) => `${orgName} requires a photo of your driver's licence.`,
  liability_waiver: () => 'Sign the required liability waiver to finish.',
}

export function RegistrationForm({
  organization,
  values,
  errors,
  isSubmitting,
  isMinor,
  age,
  emailLocked,
  phoneRequired,
  phoneLocked,
  canLinkExistingAccount,
  linkedEmail,
  onLinkExistingAccount,
  tosAccepted,
  privacyAccepted,
  legalAlreadyAccepted,
  currentStep,
  isLastStep,
  isStepSkippable,
  canGoBack,
  isCurrentStepComplete,
  goBack,
  goNext,
  onSkip,
  licenseEntries,
  driversLicense,
  onDriversLicenseChange,
  onFieldChange,
  onLicenseChange,
  onTosChange,
  onPrivacyChange,
  onSubmit,
  onLoadCurrentDocument,
  onDocumentsLoaded,
  waiver,
}: RegistrationFormProps) {
  const requirements = organization.guestRequirements
  const showSecondEmergencyContact = requirements.emergency_contact_2 === true

  function renderCurrentStep() {
    switch (currentStep.id) {
      case 'personal_info':
        return (
          <PersonalInfoSection
            values={values}
            errors={errors}
            emailLocked={emailLocked}
            phoneRequired={phoneRequired}
            phoneLocked={phoneLocked}
            canLinkExistingAccount={canLinkExistingAccount}
            linkedEmail={linkedEmail}
            onLinkExistingAccount={onLinkExistingAccount}
            isSubmitting={isSubmitting}
            tosAccepted={tosAccepted}
            privacyAccepted={privacyAccepted}
            legalAlreadyAccepted={legalAlreadyAccepted}
            onFieldChange={onFieldChange}
            onTosChange={onTosChange}
            onPrivacyChange={onPrivacyChange}
            onLoadCurrentDocument={onLoadCurrentDocument}
            onDocumentsLoaded={onDocumentsLoaded}
          />
        )
      case 'date_of_birth':
        return (
          <DateOfBirthSection
            values={values}
            errors={errors}
            isSubmitting={isSubmitting}
            isMinor={isMinor}
            age={age}
            onFieldChange={onFieldChange}
          />
        )
      case 'address':
        return (
          <AddressSection
            values={values}
            errors={errors}
            isSubmitting={isSubmitting}
            onFieldChange={onFieldChange}
          />
        )
      case 'emergency_contacts':
        return (
          <EmergencyContactsSection
            values={values}
            errors={errors}
            showSecondContact={showSecondEmergencyContact}
            isSubmitting={isSubmitting}
            onFieldChange={onFieldChange}
          />
        )
      case 'hunting_licenses':
        return (
          <LicenseCollectionSection
            requiredLicenseTypes={requirements.required_license_types ?? []}
            exemptionRules={requirements.exemption_rules ?? []}
            licenseEntries={licenseEntries}
            errors={errors}
            isSubmitting={isSubmitting}
            onLicenseChange={onLicenseChange}
          />
        )
      case 'vehicle_info':
        return (
          <VehicleInfoSection
            values={values}
            errors={errors}
            isSubmitting={isSubmitting}
            onFieldChange={onFieldChange}
          />
        )
      case 'drivers_license':
        return (
          <DriversLicenseSection
            entry={driversLicense}
            errors={errors}
            isSubmitting={isSubmitting}
            onChange={onDriversLicenseChange}
          />
        )
      case 'liability_waiver':
        return <LiabilityWaiverSection {...waiver} />
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
      className="flex flex-col gap-6"
    >
      <RegisterStepHeading
        title={currentStep.label}
        subtitle={STEP_SUBTITLES[currentStep.id]?.(organization.name)}
      />
      <RegisterStepCard>{renderCurrentStep()}</RegisterStepCard>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {canGoBack ? (
          <Button type="button" variant="outline" onClick={goBack}>
            Back
          </Button>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-3">
          {isStepSkippable ? (
            <Button type="button" variant="ghost" onClick={onSkip}>
              Skip
            </Button>
          ) : null}
          {isLastStep ? (
            <Button type="submit" variant="default" disabled={isSubmitting || !isCurrentStepComplete}>
              {isSubmitting ? 'Submitting…' : 'Complete'}
            </Button>
          ) : (
            <Button type="button" variant="default" onClick={goNext} disabled={!isCurrentStepComplete}>
              Next
            </Button>
          )}
        </div>
      </div>
    </form>
  )
}
